import React, { useState, useEffect } from 'react'
import { api } from '../../api'

const INSURANCE_CATEGORIES = [
  { value: 'General', label: 'General' },
  { value: 'Life', label: 'Life' },
  { value: 'Health', label: 'Health' }
]

export default function StepInsuranceIssuer({ onBack, onNext, token, initialIssuerKey = '', initialCategory = '', recentIssuers = [] }) {
  const [issuers, setIssuers] = useState([])
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || '')
  const [selectedIssuer, setSelectedIssuer] = useState(null)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    loadIssuers()
  }, [token])

  useEffect(() => {
    if (initialCategory && INSURANCE_CATEGORIES.some(c => c.value === initialCategory)) {
      setSelectedCategory(initialCategory)
    }
  }, [initialCategory])

  useEffect(() => {
    if (!selectedCategory || !initialIssuerKey || !issuers.length) return
    const list = issuers.filter(i =>
      (i.products || []).some(p => p.is_active !== false && (p.category || '') === selectedCategory)
    )
    const issuer = list.find(i => i._key === initialIssuerKey)
    if (issuer) setSelectedIssuer(issuer)
  }, [initialIssuerKey, issuers, selectedCategory])

  const loadIssuers = async () => {
    if (!token) return
    setLoading(true)
    try {
      const result = await api.listInsuranceIssuers(token)
      setIssuers(Array.isArray(result) ? result : [])
    } catch (error) {
      console.error('Failed to load insurance issuers:', error)
    } finally {
      setLoading(false)
    }
  }

  const hasProductInCategory = (issuer, category) =>
    (issuer.products || []).some(
      p => p.is_active !== false && (p.category || '') === category
    )

  const issuersByCategory = selectedCategory
    ? issuers.filter(i => hasProductInCategory(i, selectedCategory))
    : []

  const filteredIssuers = searchQuery
    ? issuersByCategory.filter(issuer =>
        (issuer.short_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (issuer.legal_name || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : issuersByCategory

  const recentInCategory = recentIssuers.filter(r =>
    issuersByCategory.some(i => i._key === r._key)
  )

  const handleCategoryChange = (category) => {
    setSelectedCategory(category)
    setSelectedIssuer(null)
    setSearchQuery('')
  }

  const handleNext = () => {
    if (!selectedIssuer) return
    onNext(selectedIssuer)
  }

  return (
    <div>
      <h3 className="mt-0 text-lg font-semibold text-gray-900 dark:text-gray-100">Step 4 — Select Category, then Issuer</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        First select the insurance category (General, Life, or Health). Then choose an issuer from the list shown for that category.
      </p>

      {/* 1. Category selection — must select first */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          1. Insurance category <span className="text-gray-500 dark:text-gray-400 font-normal">(General, Life or Health)</span>
        </label>
        <div className="flex flex-wrap gap-3">
          {INSURANCE_CATEGORIES.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => handleCategoryChange(value)}
              className={`px-4 py-3 rounded-xl border-2 transition-all font-medium text-sm ${
                selectedCategory === value
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!selectedCategory ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
          Select a category above to see issuers for that type.
        </div>
      ) : (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              2. Select issuer <span className="text-gray-500 dark:text-gray-400 font-normal">(for {selectedCategory})</span>
            </label>
          </div>
          {recentInCategory.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Recent issuers</label>
              <div className="flex flex-wrap gap-2">
                {recentInCategory.map(issuer => (
                  <button
                    key={issuer._key}
                    type="button"
                    onClick={() => {
                      const found = issuersByCategory.find(i => i._key === issuer._key)
                      setSelectedIssuer(found || issuer)
                    }}
                    className="px-3 py-1.5 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                  >
                    {issuer.short_name || issuer.legal_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search insurance companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Loading */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {filteredIssuers.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No insurance companies found for <strong>{selectedCategory}</strong>
                </div>
              ) : (
                filteredIssuers.map((issuer) => (
                  <button
                    key={issuer._key}
                    type="button"
                    onClick={() => setSelectedIssuer(issuer)}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      selectedIssuer?._key === issuer._key
                        ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-base font-bold text-gray-900 dark:text-white">{issuer.short_name || issuer.legal_name}</h4>
                        {issuer.legal_name && issuer.short_name !== issuer.legal_name && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{issuer.legal_name}</p>
                        )}
                      </div>
                    </div>

                    {issuer.products && issuer.products.length > 0 && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        <span className="font-medium">Products:</span>{' '}
                        {issuer.products.filter(p => p.is_active !== false && (p.category || '') === selectedCategory).length} in {selectedCategory}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}

      <div className="actions" style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={onBack} className="appearance-none border border-gray-200 dark:border-gray-700 rounded-full px-4 py-2.5 sm:px-5 sm:py-3 bg-white/85 dark:bg-gray-800/85 font-bold text-gray-900 dark:text-gray-100 hover:bg-white dark:hover:bg-gray-800 transition-colors text-sm sm:text-base">
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!selectedIssuer}
          className="appearance-none border border-gray-200 dark:border-gray-700 rounded-full px-4 py-2.5 sm:px-5 sm:py-3 font-bold bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer hover:shadow-md transition-shadow disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
